import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { H5, InputGroup, Spinner } from '@blueprintjs/core';
import { buildOffsetPaginationProps } from '@gonfalon/pagination/buildOffsetPaginationProps';
import { Pagination } from '@gonfalon/pagination/component';
import { Button } from '@launchpad-ui/components';
import type { TeamCustomRole, TeamCustomRoles } from 'launchdarkly-api-typescript';
import { debounce } from 'lodash';

import { DefaultCallout, ErrorCallout, Module, ModuleContent, Table } from '../../../shared';
import { Box, Flex } from '../../../shared/components';
import { observableToPromise } from '../../../shared/observableToPromise';
import {
  fetchMembersByUrl,
  fetchTeamRolesByUrl,
  filterMembers,
  getTeamWithRolesAndAttributes,
  type MemberResponse,
} from '../../api';
import { useTeams } from '../../hooks/useTeams';
import { type Account, type CustomRole, type Member, type RoleAttributeMap } from '../../model';
import { extractLinkHrefs, type LinkHrefs } from '../../utils';
import { CustomRoleDetails } from '../CustomRoleDetails';

import './TeamsPanel.scss';

type TeamRow = {
  key: string;
  name: string;
  memberCount: number;
  roles?: TeamCustomRoles;
};

type TeamsPanelProps = {
  customRoles: { [roleId: string]: CustomRole };
  account: Account;
};

type TeamMemberData = {
  members: Member[];
  totalCount: number;
  links: LinkHrefs;
};

type TeamRolesData = {
  items: TeamCustomRole[];
  totalCount: number;
  links: LinkHrefs;
};

const PAGE_SIZE = 20;
const ROLE_CHIP_LIMIT = 5;

const hasRequiredTeamFields = (team: { key?: string; name?: string }): team is { key: string; name: string } =>
  Boolean(team.key && team.name);

export const TeamsPanel = ({ customRoles, account }: TeamsPanelProps) => {
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const [teamMemberData, setTeamMemberData] = useState<{ [teamKey: string]: TeamMemberData }>({});
  const [loadingTeams, setLoadingTeams] = useState<Set<string>>(new Set());
  const [teamMemberErrors, setTeamMemberErrors] = useState<{ [teamKey: string]: string }>({});
  const [teamRoleAttributes, setTeamRoleAttributes] = useState<{ [teamKey: string]: RoleAttributeMap }>({});
  const [loadingTeamRoleAttributes, setLoadingTeamRoleAttributes] = useState<Set<string>>(new Set());
  const [teamSearch, setTeamSearch] = useState('');
  const [query, setQuery] = useState('');
  const [pageOffset, setPageOffset] = useState(0);
  const [teamSortField, setTeamSortField] = useState<'name' | 'memberCount'>('name');
  const [teamSortDirection, setTeamSortDirection] = useState<'asc' | 'desc'>('asc');
  const [expandedTeamRoles, setExpandedTeamRoles] = useState<Set<string>>(new Set());
  const [teamRolesData, setTeamRolesData] = useState<{ [teamKey: string]: TeamRolesData }>({});
  const [loadingTeamRoles, setLoadingTeamRoles] = useState<Set<string>>(new Set());

  const debouncedSetQuery = useMemo(
    () =>
      debounce((value: string) => {
        setQuery(value.trim());
        setPageOffset(0);
      }, 300),
    [],
  );
  useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery]);

  const sort = `${teamSortDirection === 'desc' ? '-' : ''}${teamSortField}`;
  const {
    data: teamsPage,
    isLoading,
    isPlaceholderData,
    error: teamsError,
  } = useTeams(account, { query: query || undefined, sort, limit: PAGE_SIZE, offset: pageOffset });
  const teams: TeamRow[] = (teamsPage?.items ?? []).filter(hasRequiredTeamFields).map((team) => ({
    key: team.key,
    name: team.name,
    memberCount: team.members?.totalCount ?? 0,
    roles: team.roles,
  }));
  const teamsTotal = teamsPage?.totalCount ?? 0;

  const getOffsetFromUrl = (url?: string) => {
    if (!url) {
      return 0;
    }

    try {
      const parsedUrl = new URL(`${window.location.protocol}//${window.location.host}${url}`);
      const offsetParam = parsedUrl.searchParams.get('offset');
      const offsetValue = offsetParam ? parseInt(offsetParam, 10) : 0;
      return Number.isNaN(offsetValue) ? 0 : offsetValue;
    } catch (_error) {
      return 0;
    }
  };

  const goToMembersPage = useCallback(
    async (teamKey: string, url?: string | null) => {
      if (!url) {
        return;
      }

      setLoadingTeams((prev) => new Set(prev).add(teamKey));
      setTeamMemberErrors((prev) => {
        const next = { ...prev };
        delete next[teamKey];
        return next;
      });
      try {
        const response: MemberResponse = await observableToPromise(fetchMembersByUrl(account, url));
        const newMembers = response.entities?.members ? Object.values(response.entities.members) : [];
        const totalCount = response.result?.totalCount || 0;

        setTeamMemberData((prev) => ({
          ...prev,
          [teamKey]: {
            members: newMembers,
            totalCount,
            links: extractLinkHrefs(response.result?._links),
          },
        }));
      } catch (error) {
        console.error(`Failed to fetch members for team ${teamKey}:`, error);
        setTeamMemberErrors((prev) => ({
          ...prev,
          [teamKey]: 'Failed to load team members. Please try again.',
        }));
      } finally {
        setLoadingTeams((prev) => {
          const next = new Set(prev);
          next.delete(teamKey);
          return next;
        });
      }
    },
    [account],
  );

  const toggleTeamMembers = useCallback(
    async (teamKey: string) => {
      if (expandedTeams.has(teamKey)) {
        // Collapse
        setExpandedTeams((prev) => {
          const next = new Set(prev);
          next.delete(teamKey);
          return next;
        });
        return;
      }

      // Expand and fetch if not already loaded
      if (!teamMemberData[teamKey]) {
        setLoadingTeams((prev) => new Set(prev).add(teamKey));
        setTeamMemberErrors((prev) => {
          const next = { ...prev };
          delete next[teamKey];
          return next;
        });
        try {
          const response: MemberResponse = await observableToPromise(filterMembers(account, '', teamKey));
          const membersArray = response.entities?.members ? Object.values(response.entities.members) : [];
          const totalCount = response.result?.totalCount || 0;

          setTeamMemberData((prev) => ({
            ...prev,
            [teamKey]: { members: membersArray, totalCount, links: extractLinkHrefs(response.result?._links) },
          }));
          setExpandedTeams((prev) => new Set(prev).add(teamKey));
        } catch (error) {
          console.error(`Failed to fetch members for team ${teamKey}:`, error);
          setTeamMemberErrors((prev) => ({
            ...prev,
            [teamKey]: 'Failed to load team members. Please try again.',
          }));
          setExpandedTeams((prev) => new Set(prev).add(teamKey));
        } finally {
          setLoadingTeams((prev) => {
            const next = new Set(prev);
            next.delete(teamKey);
            return next;
          });
        }
        return;
      }

      setExpandedTeams((prev) => new Set(prev).add(teamKey));
    },
    [expandedTeams, teamMemberData, account],
  );

  const fetchTeamRoleAttributes = useCallback(
    async (teamKey: string) => {
      if (teamRoleAttributes[teamKey] || loadingTeamRoleAttributes.has(teamKey)) {
        return;
      }

      setLoadingTeamRoleAttributes((prev) => new Set(prev).add(teamKey));
      try {
        const response = await observableToPromise(getTeamWithRolesAndAttributes(teamKey, account));
        setTeamRoleAttributes((prev) => ({
          ...prev,
          [teamKey]: response.roleAttributes ?? {},
        }));
      } catch (error) {
        console.error(`Failed to fetch role attributes for team ${teamKey}:`, error);
      } finally {
        setLoadingTeamRoleAttributes((prev) => {
          const next = new Set(prev);
          next.delete(teamKey);
          return next;
        });
      }
    },
    [account, loadingTeamRoleAttributes, teamRoleAttributes],
  );

  const getTeamRolesData = useCallback(
    (team: TeamRow): TeamRolesData => {
      const existing = teamRolesData[team.key];
      if (existing) {
        return existing;
      }

      const items = team.roles?.items ?? [];
      const totalCount = team.roles?.totalCount ?? items.length;
      return {
        items,
        totalCount,
        links: extractLinkHrefs(team.roles?._links),
      };
    },
    [teamRolesData],
  );

  const fetchAllTeamRoles = useCallback(
    async (teamKey: string, roleData: TeamRolesData) => {
      if (!roleData.links.next || loadingTeamRoles.has(teamKey)) {
        return;
      }

      setLoadingTeamRoles((prev) => new Set(prev).add(teamKey));
      try {
        let nextUrl: string | undefined = roleData.links.next;
        let items = [...roleData.items];
        let { totalCount, links } = roleData;

        while (nextUrl) {
          const currentUrl = nextUrl;
          // oxlint-disable-next-line no-await-in-loop
          const response = await observableToPromise(fetchTeamRolesByUrl(account, currentUrl));
          items = items.concat(response.items ?? []);
          totalCount = response.totalCount ?? totalCount;
          links = extractLinkHrefs(response._links);
          nextUrl = links.next;
        }

        setTeamRolesData((prev) => ({
          ...prev,
          [teamKey]: {
            items,
            totalCount,
            links,
          },
        }));
      } catch (error) {
        console.error(`Failed to fetch roles for team ${teamKey}:`, error);
      } finally {
        setLoadingTeamRoles((prev) => {
          const next = new Set(prev);
          next.delete(teamKey);
          return next;
        });
      }
    },
    [account, loadingTeamRoles],
  );

  const toggleTeamRoles = useCallback(
    (teamKey: string, roleData: TeamRolesData) => {
      const isExpanded = expandedTeamRoles.has(teamKey);
      setExpandedTeamRoles((prev) => {
        const next = new Set(prev);
        if (isExpanded) {
          next.delete(teamKey);
        } else {
          next.add(teamKey);
        }
        return next;
      });

      if (!isExpanded && roleData.totalCount > roleData.items.length) {
        fetchAllTeamRoles(teamKey, roleData).catch(() => undefined);
      }
    },
    [expandedTeamRoles, fetchAllTeamRoles],
  );

  // Helper to get full CustomRole from role key
  const getCustomRole = (roleKey: string): CustomRole | undefined =>
    Object.values(customRoles).find((r) => r.key === roleKey);

  const pagination = buildOffsetPaginationProps(teamsTotal, PAGE_SIZE, pageOffset, setPageOffset);
  const showPagination = teamsTotal > PAGE_SIZE;

  const handleSort = (field: 'name' | 'memberCount') => {
    setPageOffset(0);
    if (teamSortField === field) {
      setTeamSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setTeamSortField(field);
    setTeamSortDirection('asc');
  };

  const renderSortIndicator = (field: 'name' | 'memberCount') => {
    if (teamSortField !== field) {
      return null;
    }
    return <span className="rolesAndTeams__sortIndicator">{teamSortDirection === 'asc' ? '▲' : '▼'}</span>;
  };

  const heading = isLoading ? 'Loading teams...' : `${teamsTotal} ${teamsTotal === 1 ? 'team' : 'teams'} found`;
  const toolbar = (
      <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap" className="rolesAndTeams__toolbar">
        <Box>
          <H5>{heading}</H5>
          {query && <div className="rolesAndTeams__results">Showing teams matching "{query}"</div>}
        </Box>
        <InputGroup
          leftIcon="search"
          placeholder="Search teams..."
          value={teamSearch}
          onChange={(event) => {
            setTeamSearch(event.target.value);
            debouncedSetQuery(event.target.value);
          }}
          className="rolesAndTeams__search"
        />
      </Flex>
  );

  if (isLoading) {
    return <Box p={2}>{toolbar}</Box>;
  }

  if (teamsError) {
    return (
      <Box p={2}>
        {toolbar}
        <ErrorCallout heading="Failed to load teams">Teams could not be loaded. Please try refreshing the page.</ErrorCallout>
      </Box>
    );
  }

  if (teamsTotal === 0 && !query && !isPlaceholderData) {
    return <DefaultCallout>No teams found for this account.</DefaultCallout>;
  }

  return (
    <Box p={2}>
      {toolbar}
      <Module>
        <ModuleContent snug>
          <Table className="rolesAndTeams__table rolesAndTeams__table--teams">
            <colgroup>
              <col className="rolesAndTeams__col rolesAndTeams__col--name" />
              <col className="rolesAndTeams__col rolesAndTeams__col--members" />
              <col className="rolesAndTeams__col rolesAndTeams__col--roles" />
            </colgroup>
            <thead>
              <tr>
                <td>
                  <button type="button" className="rolesAndTeams__sortButton" onClick={() => handleSort('name')}>
                    Name {renderSortIndicator('name')}
                  </button>
                </td>
                <td>
                  <button type="button" className="rolesAndTeams__sortButton" onClick={() => handleSort('memberCount')}>
                    Members {renderSortIndicator('memberCount')}
                  </button>
                </td>
                <td>Roles</td>
              </tr>
            </thead>
            <tbody>
              {teams.length === 0 ? (
                <tr>
                  <td colSpan={3} className="bp4-text-muted">
                    No teams match your search.
                  </td>
                </tr>
              ) : (
                teams.map((team) => {
                  const data = teamMemberData[team.key];
                  const members = data?.members || [];
                  const totalCount = data?.totalCount || team.memberCount;
                  const roleAttributes = teamRoleAttributes[team.key];
                  const roleData = getTeamRolesData(team);
                  const rolesExpanded = expandedTeamRoles.has(team.key);
                  const visibleRoles = rolesExpanded ? roleData.items : roleData.items.slice(0, ROLE_CHIP_LIMIT);
                  const remainingRoles = Math.max(roleData.totalCount - visibleRoles.length, 0);
                  const showRoleToggle = roleData.totalCount > ROLE_CHIP_LIMIT;
                  const isLoadingRoles = loadingTeamRoles.has(team.key);
                  const links = data?.links;
                  const memberError = teamMemberErrors[team.key];
                  const hasPrevMembersPage = Boolean(links?.prev);
                  const hasNextMembersPage = Boolean(links?.next);
                  const offset = getOffsetFromUrl(links?.self);
                  const fromCount = totalCount === 0 ? 0 : offset + 1;
                  const toCount = Math.min(offset + members.length, totalCount);
                  const showMemberPagination = totalCount > members.length;
                  let memberContent;
                  if (memberError) {
                    memberContent = <ErrorCallout heading="Unable to load members">{memberError}</ErrorCallout>;
                  } else if (members.length > 0) {
                    memberContent = (
                      <div>
                        <strong>
                          Team Members ({fromCount}–{toCount} of {totalCount}):
                        </strong>
                        <ul className="rolesAndTeams__memberList">
                          {members.map((member) => (
                            <li key={member._id}>
                              {member.firstName || member.lastName
                                ? `${member.firstName || ''} ${member.lastName || ''}`.trim()
                                : member.email}
                              <span className="bp4-text-muted"> ({member.email})</span>
                            </li>
                          ))}
                        </ul>
                        {showMemberPagination && (
                          <Flex justifyContent="flex-end" className="rolesAndTeams__memberPagination">
                            <Pagination
                              from={fromCount}
                              to={toCount}
                              total={totalCount}
                              onFirstClick={
                                hasPrevMembersPage && links?.first
                                  ? () => goToMembersPage(team.key, links.first)
                                  : undefined
                              }
                              onPreviousClick={
                                hasPrevMembersPage && links?.prev
                                  ? () => goToMembersPage(team.key, links.prev)
                                  : undefined
                              }
                              onNextClick={
                                hasNextMembersPage && links?.next
                                  ? () => goToMembersPage(team.key, links.next)
                                  : undefined
                              }
                              onLastClick={
                                hasNextMembersPage && links?.last
                                  ? () => goToMembersPage(team.key, links.last)
                                  : undefined
                              }
                            />
                          </Flex>
                        )}
                      </div>
                    );
                  } else {
                    memberContent = <span className="bp4-text-muted">No members found</span>;
                  }
                  let roleToggleLabel = `Show ${remainingRoles} more`;
                  if (rolesExpanded) {
                    roleToggleLabel = 'Show less';
                  }
                  if (isLoadingRoles) {
                    roleToggleLabel = 'Loading roles...';
                  }

                  return (
                    <Fragment key={team.key}>
                      <tr>
                        <td>
                          <strong>{team.name}</strong>
                          <br />
                          <small className="bp4-text-muted">{team.key}</small>
                        </td>
                        <td>
                          <Button
                            variant="minimal"
                            size="small"
                            onPress={() => toggleTeamMembers(team.key)}
                            isDisabled={loadingTeams.has(team.key)}
                          >
                            {loadingTeams.has(team.key) && !data ? (
                              <Spinner size={12} />
                            ) : (
                              <>
                                {team.memberCount} {team.memberCount === 1 ? 'member' : 'members'}
                                {expandedTeams.has(team.key) ? ' ▲' : ' ▼'}
                              </>
                            )}
                          </Button>
                        </td>
                        <td>
                          {roleData.totalCount > 0 ? (
                            <div className="rolesAndTeams__rolesList">
                              {visibleRoles.map((teamRole, index) => {
                                const roleKey = teamRole.key;
                                if (!roleKey) {
                                  return null;
                                }

                                const fullRole = getCustomRole(roleKey);
                                if (fullRole) {
                                  return (
                                    <CustomRoleDetails
                                      key={roleKey}
                                      role={fullRole}
                                      roleType="team"
                                      teamKey={team.key}
                                      teamRoleAttributes={roleAttributes}
                                      onTeamDataNeeded={fetchTeamRoleAttributes}
                                      showTeamRoleCallout
                                    />
                                  );
                                }

                                return (
                                  <span key={`${roleKey}-${index}`} className="bp4-text-muted">
                                    {teamRole.name ?? roleKey}{' '}
                                  </span>
                                );
                              })}
                              {showRoleToggle && (
                                <Button
                                  variant="minimal"
                                  size="small"
                                  className="rolesAndTeams__rolesToggle"
                                  onPress={() => toggleTeamRoles(team.key, roleData)}
                                  isDisabled={isLoadingRoles}
                                >
                                  {roleToggleLabel}
                                </Button>
                              )}
                            </div>
                          ) : (
                            'None'
                          )}
                        </td>
                      </tr>
                      {expandedTeams.has(team.key) && (
                        <tr>
                          <td colSpan={3} className="rolesAndTeams__expandedCell">
                            {memberContent}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </Table>
        </ModuleContent>
      </Module>
      {showPagination && (
        <Flex justifyContent="flex-end" className="rolesAndTeams__pagination">
          <Pagination {...pagination} />
        </Flex>
      )}
    </Box>
  );
};
